// src/hooks/useEstimateApi.ts
import { useMemo } from "react";
import { useApi } from "./useApi.ts";
import { EstimateDto } from "../contracts/estimates/types";
import {
  EstimateSearchCriteria,
  EstimateSearchPagination,
  EstimateSearchResponse,
} from "../contracts/estimates/searchTypes";
import { saveAs } from "file-saver";

const API_BASE = process.env.REACT_APP_API_BASE as string;

type Template = "jinyoung" | "mars" | "mun" | "comparison";

export const useEstimateApi = () => {
  const { apiFetch } = useApi();

  // 반환 객체와 모든 내부 함수를 useMemo로 메모이제이션하여 ref 안정화한다.
  // 소비자가 useEffect/useCallback dep로 함수를 사용해도 무한 루프가 발생하지 않게 한다.
  return useMemo(() => {
    const filenameXlsx = (form: EstimateDto) =>
      `견적서_${form.cover.companyName}_${form.cover.estimateDate}.xlsx`;
    const filenamePdf = (form: EstimateDto) =>
      `견적서_${form.cover.companyName}_${form.cover.estimateDate}.pdf`;

    // ---- Generic helpers ----
    const postBlob = async (path: string, form: EstimateDto): Promise<Blob> => {
    const res = await apiFetch(path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (!res.ok) throw new Error(`server ${res.status}`);
    return res.blob();
  };

  const postPdfBlob = async (path: string, form: EstimateDto): Promise<Blob> => {
    const res = await apiFetch(path, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/pdf",
      },
      body: JSON.stringify(form),
    });
    if (!res.ok) throw new Error(`server ${res.status}`);
    const buf = await res.arrayBuffer();
    return new Blob([buf], { type: "application/pdf" });
  };

  const downloadAndSave = async (
    path: string,
    form: EstimateDto,
    filename: string
  ): Promise<void> => {
    const blob = await postBlob(path, form);
    saveAs(blob, filename);
  };

  // ---- Template-keyed factories ----
  const previewCover = (tpl: Template) => (form: EstimateDto) =>
    postBlob(`${API_BASE}/estimate/cover/preview/${tpl}`, form);

  const previewContent = (tpl: Template) => (form: EstimateDto) =>
    postBlob(`${API_BASE}/estimate/content/preview/${tpl}`, form);

  const downloadXlsx = (tpl: Template) => (form: EstimateDto) =>
    downloadAndSave(`${API_BASE}/estimate/download/${tpl}/xlsx`, form, filenameXlsx(form));

  const downloadPdf = (tpl: Template) => (form: EstimateDto) =>
    downloadAndSave(`${API_BASE}/estimate/download/${tpl}/pdf`, form, filenamePdf(form));

  const previewFullPdf = (tpl: Template) => (form: EstimateDto) =>
    postPdfBlob(`${API_BASE}/estimate/download/${tpl}/pdf`, form);

  // ---- CRUD / search (unchanged behavior) ----
  const createEstimate = async (form: EstimateDto) => {
    const res = await apiFetch(`${API_BASE}/estimate/insert`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (!res.ok) throw new Error("server " + res.status);
    return res.blob();
  };

  const updateEstimate = async (form: EstimateDto) => {
    const res = await apiFetch(`${API_BASE}/estimate/${form.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    });
    if (!res.ok) throw new Error("server " + res.status);
    return res.blob();
  };

  const deleteEstimate = async (id: String) => {
    const res = await apiFetch(`${API_BASE}/estimate/${id}`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) {
      const errData = await res.json();
      throw new Error(errData.error || "삭제 실패");
    }
    const data = await res.json();
    console.log("삭제 결과:", data);
    return data;
  };

  const searchEstimates = async (
    criteria: EstimateSearchCriteria,
    pagination: EstimateSearchPagination
  ): Promise<EstimateSearchResponse> => {
    const params = new URLSearchParams();
    const merged: Record<string, unknown> = { ...criteria, ...pagination };
    Object.entries(merged).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== "") {
        params.set(k, String(v));
      }
    });
    const res = await apiFetch(`${API_BASE}/estimate?${params.toString()}`);
    if (!res.ok) throw new Error(`server ${res.status}`);
    return res.json();
  };

  const autoGrow = (el: HTMLTextAreaElement) => {
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };

  // ---- Backwards-compatible alias surface ----
  // 호출처 코드 변경 0건을 목표로, 기존 export 이름을 그대로 유지한다.
  // 새 코드는 previewCover("jinyoung") 같은 직접 호출을 권장.
  return {
    // jinyoung
    previewCoverPdf: previewCover("jinyoung"),
    previewContentPdf: previewContent("jinyoung"),
    downloadXlsx: downloadXlsx("jinyoung"),
    previewFullPdf: previewFullPdf("jinyoung"),
    downloadPdf: downloadPdf("jinyoung"),

    // mun
    previewCoverPdfMun: previewCover("mun"),
    previewContentPdfMun: previewContent("mun"),
    downloadXlsxMun: downloadXlsx("mun"),
    previewFullPdfMun: previewFullPdf("mun"),
    downloadPdfMun: downloadPdf("mun"),

    // comparison (no cover preview)
    previewContentPdfComparison: previewContent("comparison"),
    downloadXlsxComparison: downloadXlsx("comparison"),
    downloadPdfComparison: downloadPdf("comparison"),

    // CRUD/search/util
    createEstimate,
    updateEstimate,
    deleteEstimate,
    searchEstimates,
    autoGrow,
  };
  }, [apiFetch]);
};
